import { z } from "zod";

declare const opaqueBrand: unique symbol;
export type Opaque<B extends string> = { readonly [opaqueBrand]: B };

declare const internalTwin: unique symbol;
/** A public id brand bound to the internal id it stands for; see {@link publicIdOf}. */
export type PublicOpaque<B extends string, I> = Opaque<B> & {
  readonly [internalTwin]: I;
};
export type InternalTwinOf<P> = P extends { readonly [internalTwin]: infer I }
  ? I
  : never;

/**
 * The widest "id-like" type: a plain string or any opaque branded id (every
 * `Opaque<"X">` is assignable to `Opaque<string>`). Use for generic constraints
 * over id types and for polymorphic id boundaries that accept any entity's id.
 */
export type AnyId = string | Opaque<string>;

interface IdCompanion<T> {
  readonly unsafeCreate: <U extends string>(
    value: U extends T ? never : U,
  ) => T;
  /**
   * Explicitly convert an opaque branded ID back to a plain string.
   * Use at transport boundaries (Mongo criteria, serialization, regex, string ops).
   */
  readonly unwrap: (id: T) => string;
}

export function opaqueId<B extends string>(
  brand: B,
  prefix: string,
): {
  schema: z.ZodType<Opaque<B>, string>;
  companion: IdCompanion<Opaque<B>> & { readonly prefix: string };
};
export function opaqueId<B extends string>(
  brand: B,
): {
  schema: z.ZodType<Opaque<B>, string>;
  companion: IdCompanion<Opaque<B>>;
};
export function opaqueId<B extends string>(_brand: B, prefix?: string) {
  const { schema, companion } = retypedId<Opaque<B>>();
  return {
    schema,
    companion: {
      ...companion,
      // `{prefix}-{uuid}` is the shape of this entity's internal id; carried on the
      // companion so `generatePrefixedId(Brand)` derives it (only prefixed brands mint ids).
      ...(prefix !== undefined ? { prefix } : {}),
    },
  };
}

/**
 * Declares the public id that stands for an internal one. The internal twin rides
 * on the public brand's type, so a transport ref to it is checked against the twin.
 */
export function publicIdOf<I, B extends string>(
  _internal: IdCompanion<I>,
  _brand: B,
): {
  schema: z.ZodType<PublicOpaque<B, I>, string>;
  companion: IdCompanion<PublicOpaque<B, I>>;
} {
  return retypedId<PublicOpaque<B, I>>();
}

function retypedId<Branded>(): {
  schema: z.ZodType<Branded, string>;
  companion: IdCompanion<Branded>;
} {
  // Retype-only codec: the schema stays a real ZodString at runtime (so JSON,
  // Mongo, and Set/Map value-equality all keep working on the underlying
  // string), while its output type is the opaque brand. No .transform — that
  // would make it a ZodPipe and break z.toJSONSchema / ZodString introspection.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required to retype the ZodString output to the opaque brand
  const schema = z.string() as unknown as z.ZodType<Branded, string>;

  const companion = {
    unsafeCreate<U extends string>(
      value: U extends Branded ? never : U,
    ): Branded {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction from trusted sources
      return value as unknown as Branded;
    },

    unwrap(id: Branded): string {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Runtime no-op: the brand is a string underneath
      return id as unknown as string;
    },
  };

  return { schema, companion };
}

/**
 * Companion for the polymorphic {@link AnyId} boundary — accepts any id (plain
 * string or opaque brand) and unwraps to its underlying string. Use where the
 * subject id is genuinely heterogeneous (e.g. the enrichment activity subject),
 * so the specific brand isn't known at the boundary.
 */
export const anyIdCompanion = {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Runtime no-op: every AnyId is a string underneath
  unwrap: (id: AnyId): string => id as unknown as string,
};

/**
 * Schema for the polymorphic {@link AnyId} boundary. Stays a real ZodString at
 * runtime (so JSON/Mongo keep working) while typing its output as {@link AnyId},
 * so a heterogeneous id field (e.g. a message's `relatedEntityId`) accepts any
 * brand without unwrapping. Same retype-only trick as {@link opaqueId}.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required to retype the ZodString output to AnyId
export const AnyIdSchema = z.string() as unknown as z.ZodType<AnyId, string>;

/**
 * Format of a public id. Lives here because {@link opaqueId} is retype-only —
 * every `*IdPublicSchema` below accepts any string at runtime — so this is the
 * only thing that checks the characters. Use it wherever an untrusted public id
 * arrives and a malformed one is a meaningful outcome rather than a crash.
 */
export const IdPublicSchema = z
  .string()
  .regex(
    /^[a-z0-9_-]+$/,
    "Public ID must contain only lowercase letters, digits, underscores, or hyphens",
  );

const _userId = opaqueId("UserId", "usr");
export const UserIdSchema = _userId.schema;
export type UserId = z.infer<typeof UserIdSchema>;
export const UserId = _userId.companion;

const _userIdPublic = publicIdOf(_userId.companion, "UserIdPublic");
export const UserIdPublicSchema = _userIdPublic.schema;
export type UserIdPublic = z.infer<typeof UserIdPublicSchema>;
export const UserIdPublic = _userIdPublic.companion;

const _eventId = opaqueId("EventId", "evt");
export const EventIdSchema = _eventId.schema;
export type EventId = z.infer<typeof EventIdSchema>;
export const EventId = _eventId.companion;

const _eventIdPublic = publicIdOf(_eventId.companion, "EventIdPublic");
export const EventIdPublicSchema = _eventIdPublic.schema;
export type EventIdPublic = z.infer<typeof EventIdPublicSchema>;
export const EventIdPublic = _eventIdPublic.companion;

const _eventSlug = opaqueId("EventSlug");
export const EventSlugSchema = _eventSlug.schema;
export type EventSlug = z.infer<typeof EventSlugSchema>;
export const EventSlug = _eventSlug.companion;

const _eventIdPublicOrSlug = opaqueId("EventIdPublicOrSlug");
export const EventIdPublicOrSlugSchema = _eventIdPublicOrSlug.schema;
export type EventIdPublicOrSlug = z.infer<typeof EventIdPublicOrSlugSchema>;
export const EventIdPublicOrSlug = _eventIdPublicOrSlug.companion;

/**
 * The matching engine's key for a person. Its own id on purpose: the engine has
 * no notion of a participant, so whoever hands it people translates at the
 * boundary through {@link EventParticipantId.toPerson} /
 * {@link EventParticipantId.fromPerson} — the only sanctioned crossing.
 */
const _personId = opaqueId("PersonId");
export const PersonIdSchema = _personId.schema;
export type PersonId = z.infer<typeof PersonIdSchema>;
export const PersonId = _personId.companion;

/**
 * The person key a match selection carries when the model named a candidate the
 * pool cannot resolve. It misses every `personById` lookup, which is what drops
 * the card — so absence stays a value rather than becoming an absent field.
 */
export const NO_PERSON_ID = PersonId.unsafeCreate("");

const _eventParticipantId = opaqueId("EventParticipantId", "prt");
export const EventParticipantIdSchema = _eventParticipantId.schema;
export type EventParticipantId = z.infer<typeof EventParticipantIdSchema>;
export const EventParticipantId = {
  ..._eventParticipantId.companion,

  /**
   * The key that names this participant inside the matching engine, which has no
   * notion of a participant and keys people by this id alone. The crossing is
   * total and safe, so it is named rather than spelled `unwrap` — that one is the
   * unchecked escape hatch and says nothing about what the string becomes.
   */
  toPerson: (id: EventParticipantId): PersonId =>
    PersonId.unsafeCreate(_eventParticipantId.companion.unwrap(id)),

  /** The participant an engine person key names, the inverse of {@link toPerson}. */
  fromPerson: (personId: PersonId): EventParticipantId =>
    _eventParticipantId.companion.unsafeCreate(PersonId.unwrap(personId)),
};

const _eventParticipantIdPublic = publicIdOf(
  _eventParticipantId.companion,
  "EventParticipantIdPublic",
);
export const EventParticipantIdPublicSchema = _eventParticipantIdPublic.schema;
export type EventParticipantIdPublic = z.infer<
  typeof EventParticipantIdPublicSchema
>;
export const EventParticipantIdPublic = _eventParticipantIdPublic.companion;

const _organizationId = opaqueId("OrganizationId");
export const OrganizationIdSchema = _organizationId.schema;
export type OrganizationId = z.infer<typeof OrganizationIdSchema>;
export const OrganizationId = _organizationId.companion;

const _organizationIdPublic = opaqueId("OrganizationIdPublic");
export const OrganizationIdPublicSchema = _organizationIdPublic.schema;
export type OrganizationIdPublic = z.infer<typeof OrganizationIdPublicSchema>;
export const OrganizationIdPublic = _organizationIdPublic.companion;

const _openInvitationId = opaqueId("OpenInvitationId", "oiv");
export const OpenInvitationIdSchema = _openInvitationId.schema;
export type OpenInvitationId = z.infer<typeof OpenInvitationIdSchema>;
export const OpenInvitationId = _openInvitationId.companion;

const _openInvitationIdPublic = publicIdOf(
  _openInvitationId.companion,
  "OpenInvitationIdPublic",
);
export const OpenInvitationIdPublicSchema = _openInvitationIdPublic.schema;
export type OpenInvitationIdPublic = z.infer<
  typeof OpenInvitationIdPublicSchema
>;
export const OpenInvitationIdPublic = _openInvitationIdPublic.companion;

const _magicLinkId = opaqueId("MagicLinkId", "mlk");
export const MagicLinkIdSchema = _magicLinkId.schema;
export type MagicLinkId = z.infer<typeof MagicLinkIdSchema>;
export const MagicLinkId = _magicLinkId.companion;

const _magicLinkIdPublic = publicIdOf(
  _magicLinkId.companion,
  "MagicLinkIdPublic",
);
export const MagicLinkIdPublicSchema = _magicLinkIdPublic.schema;
export type MagicLinkIdPublic = z.infer<typeof MagicLinkIdPublicSchema>;
export const MagicLinkIdPublic = _magicLinkIdPublic.companion;

const _connectionRequestId = opaqueId("ConnectionRequestId", "crq");
export const ConnectionRequestIdSchema = _connectionRequestId.schema;
export type ConnectionRequestId = z.infer<typeof ConnectionRequestIdSchema>;
export const ConnectionRequestId = _connectionRequestId.companion;

const _connectionRequestIdPublic = publicIdOf(
  _connectionRequestId.companion,
  "ConnectionRequestIdPublic",
);
export const ConnectionRequestIdPublicSchema =
  _connectionRequestIdPublic.schema;
export type ConnectionRequestIdPublic = z.infer<
  typeof ConnectionRequestIdPublicSchema
>;
export const ConnectionRequestIdPublic = _connectionRequestIdPublic.companion;

const _customFieldDefinitionId = opaqueId("CustomFieldDefinitionId", "cfd");
export const CustomFieldDefinitionIdSchema = _customFieldDefinitionId.schema;
export type CustomFieldDefinitionId = z.infer<
  typeof CustomFieldDefinitionIdSchema
>;
export const CustomFieldDefinitionId = _customFieldDefinitionId.companion;

const _customFieldDefinitionIdPublic = opaqueId(
  "CustomFieldDefinitionIdPublic",
);
export const CustomFieldDefinitionIdPublicSchema =
  _customFieldDefinitionIdPublic.schema;
export type CustomFieldDefinitionIdPublic = z.infer<
  typeof CustomFieldDefinitionIdPublicSchema
>;
export const CustomFieldDefinitionIdPublic =
  _customFieldDefinitionIdPublic.companion;

const _enrichedCompanyId = opaqueId("EnrichedCompanyId", "eco");
export const EnrichedCompanyIdSchema = _enrichedCompanyId.schema;
export type EnrichedCompanyId = z.infer<typeof EnrichedCompanyIdSchema>;
export const EnrichedCompanyId = _enrichedCompanyId.companion;

const _enrichedCompanyIdPublic = opaqueId("EnrichedCompanyIdPublic");
export const EnrichedCompanyIdPublicSchema = _enrichedCompanyIdPublic.schema;
export type EnrichedCompanyIdPublic = z.infer<
  typeof EnrichedCompanyIdPublicSchema
>;
export const EnrichedCompanyIdPublic = _enrichedCompanyIdPublic.companion;

const _enrichedUserDataId = opaqueId("EnrichedUserDataId", "eud");
export const EnrichedUserDataIdSchema = _enrichedUserDataId.schema;
export type EnrichedUserDataId = z.infer<typeof EnrichedUserDataIdSchema>;
export const EnrichedUserDataId = _enrichedUserDataId.companion;

const _enrichedUserDataIdPublic = opaqueId("EnrichedUserDataIdPublic", "eud");
export const EnrichedUserDataIdPublicSchema = _enrichedUserDataIdPublic.schema;
export type EnrichedUserDataIdPublic = z.infer<
  typeof EnrichedUserDataIdPublicSchema
>;
export const EnrichedUserDataIdPublic = _enrichedUserDataIdPublic.companion;

const _userEnrichmentRequestId = opaqueId("UserEnrichmentRequestId", "uer");
export const UserEnrichmentRequestIdSchema = _userEnrichmentRequestId.schema;
export type UserEnrichmentRequestId = z.infer<
  typeof UserEnrichmentRequestIdSchema
>;
export const UserEnrichmentRequestId = _userEnrichmentRequestId.companion;

const _userEnrichmentRequestIdPublic = opaqueId(
  "UserEnrichmentRequestIdPublic",
);
export const UserEnrichmentRequestIdPublicSchema =
  _userEnrichmentRequestIdPublic.schema;
export type UserEnrichmentRequestIdPublic = z.infer<
  typeof UserEnrichmentRequestIdPublicSchema
>;
export const UserEnrichmentRequestIdPublic =
  _userEnrichmentRequestIdPublic.companion;

const _companyEnrichmentRequestId = opaqueId(
  "CompanyEnrichmentRequestId",
  "cer",
);
export const CompanyEnrichmentRequestIdSchema =
  _companyEnrichmentRequestId.schema;
export type CompanyEnrichmentRequestId = z.infer<
  typeof CompanyEnrichmentRequestIdSchema
>;
export const CompanyEnrichmentRequestId = _companyEnrichmentRequestId.companion;

const _companyEnrichmentRequestIdPublic = opaqueId(
  "CompanyEnrichmentRequestIdPublic",
);
export const CompanyEnrichmentRequestIdPublicSchema =
  _companyEnrichmentRequestIdPublic.schema;
export type CompanyEnrichmentRequestIdPublic = z.infer<
  typeof CompanyEnrichmentRequestIdPublicSchema
>;
export const CompanyEnrichmentRequestIdPublic =
  _companyEnrichmentRequestIdPublic.companion;

const _messageId = opaqueId("MessageId", "msg");
export const MessageIdSchema = _messageId.schema;
export type MessageId = z.infer<typeof MessageIdSchema>;
export const MessageId = _messageId.companion;

const _messageThreadId = opaqueId("MessageThreadId", "mth");
export const MessageThreadIdSchema = _messageThreadId.schema;
export type MessageThreadId = z.infer<typeof MessageThreadIdSchema>;
export const MessageThreadId = _messageThreadId.companion;

const _activityEventId = opaqueId("ActivityEventId", "act");
export const ActivityEventIdSchema = _activityEventId.schema;
export type ActivityEventId = z.infer<typeof ActivityEventIdSchema>;
export const ActivityEventId = _activityEventId.companion;

const _otpId = opaqueId("OTPId", "otp");
export const OTPIdSchema = _otpId.schema;
export type OTPId = z.infer<typeof OTPIdSchema>;
export const OTPId = _otpId.companion;

const _sessionId = opaqueId("SessionId", "ses");
export const SessionIdSchema = _sessionId.schema;
export type SessionId = z.infer<typeof SessionIdSchema>;
export const SessionId = _sessionId.companion;

const _porterCallbackId = opaqueId("PorterCallbackId", "pcb");
export const PorterCallbackIdSchema = _porterCallbackId.schema;
export type PorterCallbackId = z.infer<typeof PorterCallbackIdSchema>;
export const PorterCallbackId = _porterCallbackId.companion;

const _porterCallbackIdPublic = opaqueId("PorterCallbackIdPublic");
export const PorterCallbackIdPublicSchema = _porterCallbackIdPublic.schema;
export type PorterCallbackIdPublic = z.infer<
  typeof PorterCallbackIdPublicSchema
>;
export const PorterCallbackIdPublic = _porterCallbackIdPublic.companion;

const _verificationCodeId = opaqueId("VerificationCodeId", "vrc");
export const VerificationCodeIdSchema = _verificationCodeId.schema;
export type VerificationCodeId = z.infer<typeof VerificationCodeIdSchema>;
export const VerificationCodeId = _verificationCodeId.companion;

const _matchingPipelineRunId = opaqueId("MatchingPipelineRunId", "mpr");
export const MatchingPipelineRunIdSchema = _matchingPipelineRunId.schema;
export type MatchingPipelineRunId = z.infer<typeof MatchingPipelineRunIdSchema>;
export const MatchingPipelineRunId = _matchingPipelineRunId.companion;

const _matchingPipelineRunIdPublic = opaqueId("MatchingPipelineRunIdPublic");
export const MatchingPipelineRunIdPublicSchema =
  _matchingPipelineRunIdPublic.schema;
export type MatchingPipelineRunIdPublic = z.infer<
  typeof MatchingPipelineRunIdPublicSchema
>;
export const MatchingPipelineRunIdPublic =
  _matchingPipelineRunIdPublic.companion;

const _suggestionOverrideId = opaqueId("SuggestionOverrideId", "sgo");
export const SuggestionOverrideIdSchema = _suggestionOverrideId.schema;
export type SuggestionOverrideId = z.infer<typeof SuggestionOverrideIdSchema>;
export const SuggestionOverrideId = _suggestionOverrideId.companion;

const _suggestionOverrideIdPublic = opaqueId(
  "SuggestionOverrideIdPublic",
  "SGO",
);
export const SuggestionOverrideIdPublicSchema =
  _suggestionOverrideIdPublic.schema;
export type SuggestionOverrideIdPublic = z.infer<
  typeof SuggestionOverrideIdPublicSchema
>;
export const SuggestionOverrideIdPublic = _suggestionOverrideIdPublic.companion;

// The stable id of a participant assumption, so callers can reference an
// assumption by id instead of duplicating its text.
const _assumptionId = opaqueId("AssumptionId", "asm");
export const AssumptionIdSchema = _assumptionId.schema;
export type AssumptionId = z.infer<typeof AssumptionIdSchema>;
export const AssumptionId = _assumptionId.companion;

const _participantLearningId = opaqueId("ParticipantLearningId", "lrn");
export const ParticipantLearningIdSchema = _participantLearningId.schema;
export type ParticipantLearningId = z.infer<typeof ParticipantLearningIdSchema>;
export const ParticipantLearningId = _participantLearningId.companion;

const _participantReasonToMeetId = opaqueId("ParticipantReasonToMeetId", "rtm");
export const ParticipantReasonToMeetIdSchema =
  _participantReasonToMeetId.schema;
export type ParticipantReasonToMeetId = z.infer<
  typeof ParticipantReasonToMeetIdSchema
>;
export const ParticipantReasonToMeetId = _participantReasonToMeetId.companion;

// The `_id` of a `notifications` row. Distinct from the row's `notificationKey`,
// which is derived from what the notification is about and carries the dedupe.
const _notificationId = opaqueId("NotificationId", "ntf");
export const NotificationIdSchema = _notificationId.schema;
export type NotificationId = z.infer<typeof NotificationIdSchema>;
export const NotificationId = _notificationId.companion;

const _eventUserId = opaqueId("EventUserId", "evu");
export const EventUserIdSchema = _eventUserId.schema;
export type EventUserId = z.infer<typeof EventUserIdSchema>;
export const EventUserId = _eventUserId.companion;
